import type { MetricNode } from '../api/types';
import type { AssuranceLevel } from './color';

export type MetricTileSortOption = 'default' | 'name_asc' | 'name_desc' | 'assurance_asc' | 'assurance_desc';

export const METRIC_TILE_SORT_OPTIONS: { value: MetricTileSortOption; label: string }[] = [
  { value: 'default', label: 'Date Added' },
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'assurance_asc', label: 'Assurance (Low–High)' },
  { value: 'assurance_desc', label: 'Assurance (High–Low)' }
];

/** Sort options for the dashboards list — assurance is not shown on overview cards. */
export const DASHBOARD_LIST_SORT_OPTIONS = METRIC_TILE_SORT_OPTIONS.filter(
  (option) => !option.value.startsWith('assurance')
);

const ASSURANCE_ORDER: Record<AssuranceLevel, number> = {
  High: 5,
  Substantial: 4,
  Moderate: 3,
  Limited: 2,
  None: 1,
  NaN: 0
};

export function sortWithPinnedFirst<T>(
  nodes: T[],
  sortBy: MetricTileSortOption,
  isPinned: (node: T) => boolean,
  compare: (a: T, b: T) => number
): T[] {
  const pinned = nodes.filter(isPinned);
  const unpinned = nodes.filter((node) => !isPinned(node));

  if (sortBy === 'default') {
    return [...pinned, ...unpinned];
  }

  return [...pinned.sort(compare), ...unpinned.sort(compare)];
}

export function compareDashboardNodes(a: MetricNode, b: MetricNode, sortBy: MetricTileSortOption): number {
  switch (sortBy) {
    case 'name_asc':
      return a.title.localeCompare(b.title);
    case 'name_desc':
      return b.title.localeCompare(a.title);
    case 'default':
    default:
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  }
}

export function sortDashboardNodes(nodes: MetricNode[], sortBy: MetricTileSortOption): MetricNode[] {
  return sortWithPinnedFirst(
    nodes,
    sortBy,
    (node) => node.is_pinned,
    (a, b) => compareDashboardNodes(a, b, sortBy)
  );
}

export function compareProcessedNodes(
  a: { name: string; assurance: AssuranceLevel },
  b: { name: string; assurance: AssuranceLevel },
  sortBy: MetricTileSortOption
): number {
  switch (sortBy) {
    case 'name_asc':
      return a.name.localeCompare(b.name);
    case 'name_desc':
      return b.name.localeCompare(a.name);
    case 'assurance_asc':
      return (ASSURANCE_ORDER[a.assurance] ?? 0) - (ASSURANCE_ORDER[b.assurance] ?? 0);
    case 'assurance_desc':
      return (ASSURANCE_ORDER[b.assurance] ?? 0) - (ASSURANCE_ORDER[a.assurance] ?? 0);
    case 'default':
    default:
      return 0;
  }
}
